/**
 * FlujoFilesManager — galería de adjuntos (fotos, videos y documentos) de un
 * flujo de caja (gasto / ingreso / intermediario). Se monta dentro del
 * `FlujoFormModal` de /flujos.
 *
 * QUÉ HACE
 *   Carga, sube, previsualiza, descarga (uno o varios), selecciona y borra
 *   los archivos asociados a un flujo. Viven en `gastos.imagenes` (JSONB) del
 *   backend y se sirven con URLs firmadas vía `/files/<token>` (6h TTL).
 *
 * Es el mismo patrón canónico de GuestFilesManager (info-huésped): zona de
 * upload con drag&drop + click + Ctrl+V, grid responsivo de tarjetas con
 * thumbnail/ícono, selección múltiple, descarga individual y ZIP, borrado
 * individual (Popconfirm) y bulk (Modal.confirm). Ver
 * docs/storage-y-visor.md §7.1.
 *
 * SEGURIDAD / VISIBILIDAD
 *   Se monta dentro del modal de /flujos, que solo abre admin/superadmin. El
 *   backend `requireRole("administrador")` es el enforce real.
 *
 * PROP `flujoId`
 *   Si es null/undefined (estamos CREANDO un flujo, sin id todavía), mostramos
 *   un Alert pidiendo guardar primero. Igual que GuestFilesManager: el upload
 *   necesita un id de destino, así mantenemos el flujo lineal crear → guardar
 *   → editar y adjuntar.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Empty,
  Image as AntImage,
  Modal as AntModal,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  FilePptOutlined,
  FileTextOutlined,
  FileWordOutlined,
  FileZipOutlined,
  PlayCircleOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import {
  DragDropUpload,
  useClipboardPaste,
} from "../../../Components/DragDropUpload";
import VideoPlayer from "../../../Components/VideoPlayer";
import { getModal, getMsg } from "../../../utils/antdStatic";
import {
  buildFlujoDownloadUrl,
  eliminarFlujoArchivo,
  listarFlujoArchivos,
  subirFlujoArchivo,
  type FlujoFile,
} from "../flujos.api";
import {
  downloadFile,
  downloadFilesAsZip,
} from "../../../utils/downloadHelpers";
import "./FlujoFilesManager.css";

const { Text, Paragraph } = Typography;

// Tipos MIME aceptados — mantener sincronizado con el `fileFilter` de
// finanzas.routes.js (constante ALLOWED_FLUJO_DOC_MIMES + image/* y video/*)
// y con las firmas de validateFileSignature (utils/fileValidation.js).
export const FLUJO_ACCEPT =
  "image/jpeg,image/png,image/webp,image/gif,image/bmp," +
  "video/mp4,video/quicktime,video/webm,video/x-msvideo,video/x-matroska,video/3gpp,video/3gpp2," +
  "application/pdf," +
  "application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
  "application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet," +
  "application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation," +
  "text/plain,text/csv";

// Límite cliente — coincide con `multer.limits.fileSize` del backend (100 MB).
export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

// Formateo humano: 1024 → "1.0 KB", etc.
function formatBytes(bytes: number): string {
  if (!bytes || bytes < 1) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unitIdx = 0;
  while (value >= 1024 && unitIdx < units.length - 1) {
    value /= 1024;
    unitIdx += 1;
  }
  return unitIdx === 0
    ? `${Math.round(value)} ${units[unitIdx]}`
    : `${value.toFixed(1)} ${units[unitIdx]}`;
}

// Ícono según mime — para documentos no-thumbnaileables.
function FileIcon({
  file,
  size = 36,
}: {
  file: FlujoFile;
  size?: number;
}): React.ReactElement {
  const mime = file.mimeType || "";
  const style = { fontSize: size, color: "#8c8c8c" };
  if (file.kind === "image")
    return <FileImageOutlined style={{ ...style, color: "#13c2c2" }} />;
  if (file.kind === "video")
    return <VideoCameraOutlined style={{ ...style, color: "#722ed1" }} />;
  if (mime === "application/pdf")
    return <FilePdfOutlined style={{ ...style, color: "#cf1322" }} />;
  if (mime.includes("word"))
    return <FileWordOutlined style={{ ...style, color: "#1677ff" }} />;
  if (mime.includes("excel") || mime.includes("spreadsheet"))
    return <FileExcelOutlined style={{ ...style, color: "#52c41a" }} />;
  if (mime.includes("powerpoint") || mime.includes("presentation"))
    return <FilePptOutlined style={{ ...style, color: "#fa8c16" }} />;
  if (mime.includes("zip"))
    return <FileZipOutlined style={{ ...style, color: "#faad14" }} />;
  if (mime.startsWith("text/"))
    return <FileTextOutlined style={{ ...style, color: "#595959" }} />;
  return <FileOutlined style={style} />;
}

// Label corto del tipo, mostrado como Tag en cada card.
function kindLabel(file: FlujoFile): { text: string; color: string } {
  if (file.kind === "image") return { text: "Imagen", color: "cyan" };
  if (file.kind === "video") return { text: "Video", color: "purple" };
  const mime = file.mimeType || "";
  if (mime === "application/pdf") return { text: "PDF", color: "red" };
  if (mime.includes("word")) return { text: "Word", color: "blue" };
  if (mime.includes("excel") || mime.includes("spreadsheet"))
    return { text: "Excel", color: "green" };
  if (mime.includes("powerpoint") || mime.includes("presentation"))
    return { text: "PPT", color: "orange" };
  if (mime.startsWith("text/")) return { text: "Texto", color: "default" };
  return { text: "Doc", color: "default" };
}

interface FlujoFilesManagerProps {
  /**
   * ID del flujo (gasto/ingreso/intermediario). Null/undefined al crear un
   * flujo nuevo — el componente muestra un Alert pidiendo guardar primero.
   */
  flujoId: number | string | null | undefined;
  /**
   * `true` mientras el modal padre esté abierto. Controla el listener de paste
   * (Ctrl+V) para que solo se active cuando el admin edita este flujo.
   */
  modalOpen: boolean;
}

const FlujoFilesManager: React.FC<FlujoFilesManagerProps> = ({
  flujoId,
  modalOpen,
}) => {
  const [files, setFiles] = useState<FlujoFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const uploadQueue = useRef<Promise<void>>(Promise.resolve());
  const pendingUploads = useRef(0);
  // Set de `key` de archivos que están siendo eliminados (overlay individual).
  const [deletingKeys, setDeletingKeys] = useState<Set<string>>(new Set());
  // Set de `key` seleccionados — controla las acciones bulk de la toolbar.
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewVideo, setPreviewVideo] = useState<FlujoFile | null>(null);
  const [downloadingAll, setDownloadingAll] = useState(false);

  // ── Carga inicial ─────────────────────────────────────────────────────
  // Re-fetcheamos cada vez que cambia el id o se abre el modal, para que las
  // URLs firmadas no caduquen entre sesiones largas del admin.
  const reload = useCallback(async () => {
    if (!flujoId) {
      setFiles([]);
      return;
    }
    setLoading(true);
    try {
      const list = await listarFlujoArchivos(flujoId);
      setFiles(list);
    } finally {
      setLoading(false);
    }
  }, [flujoId]);

  useEffect(() => {
    if (modalOpen && flujoId) {
      reload();
    } else {
      // Al cerrar el modal limpiamos selección/estado para que el siguiente
      // abrir empiece fresh (evita "fugas" entre flujos editados en secuencia).
      setSelectedKeys(new Set());
      setPreviewImage(null);
      setPreviewVideo(null);
    }
  }, [modalOpen, flujoId, reload]);

  // ── Upload ────────────────────────────────────────────────────────────
  // Subimos archivos uno a uno (no en paralelo) para feedback claro al
  // arrastrar varios. Si una falla, seguimos con las siguientes.
  const handleUpload = useCallback(
    async (file: File) => {
      if (!flujoId) {
        getMsg()?.warning("Guarda el flujo antes de adjuntar archivos");
        return;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        getMsg()?.error(
          `"${file.name}" pesa ${formatBytes(file.size)} — el máximo permitido es ${formatBytes(MAX_UPLOAD_BYTES)}.`,
        );
        return;
      }
      pendingUploads.current += 1;
      setUploading(true);
      uploadQueue.current = uploadQueue.current.catch(() => {}).then(async () => {
        try {
          const uploaded = await subirFlujoArchivo(flujoId, file);
          if (uploaded) {
            setFiles((prev) => [...prev, uploaded]);
            getMsg()?.success(`"${file.name}" subido correctamente`);
          } else getMsg()?.error(`No se pudo subir "${file.name}"`);
        } catch {
          getMsg()?.error(`No se pudo subir "${file.name}"`);
        } finally {
          pendingUploads.current -= 1;
          if (!pendingUploads.current) setUploading(false);
        }
      });
      await uploadQueue.current;
    },
    [flujoId],
  );

  // Paste handler — gateado a `modalOpen && !!flujoId`.
  useClipboardPaste({
    active: modalOpen && !!flujoId,
    onFile: handleUpload,
    accept: FLUJO_ACCEPT,
  });

  // ── Selección ─────────────────────────────────────────────────────────
  const toggleSelected = useCallback((key: string, checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const allSelected = files.length > 0 && selectedKeys.size === files.length;
  const partiallySelected = selectedKeys.size > 0 && !allSelected;

  const toggleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedKeys(checked ? new Set(files.map((f) => f.key)) : new Set());
    },
    [files],
  );

  // ── Acciones — descarga ──────────────────────────────────────────────
  const handleDownloadOne = useCallback(async (file: FlujoFile) => {
    try {
      await downloadFile(buildFlujoDownloadUrl(file), file.originalName);
    } catch (e: any) {
      getMsg()?.error(e?.message || "No se pudo descargar el archivo");
    }
  }, []);

  const downloadSelectedAsZip = useCallback(async () => {
    const targets = files.filter((f) => selectedKeys.has(f.key));
    if (targets.length === 0) return;
    setDownloadingAll(true);
    try {
      await downloadFilesAsZip(
        targets.map((f) => ({ url: buildFlujoDownloadUrl(f), name: f.originalName })),
        `flujo-${flujoId}-adjuntos-seleccion`,
      );
      getMsg()?.success(`${targets.length} archivo(s) empaquetados en ZIP`);
    } catch (e: any) {
      getMsg()?.error(e?.message || "No se pudo armar el ZIP");
    } finally {
      setDownloadingAll(false);
    }
  }, [files, selectedKeys, flujoId]);

  const downloadAllAsZip = useCallback(async () => {
    if (files.length === 0) return;
    setDownloadingAll(true);
    try {
      await downloadFilesAsZip(
        files.map((f) => ({ url: buildFlujoDownloadUrl(f), name: f.originalName })),
        `flujo-${flujoId}-adjuntos`,
      );
      getMsg()?.success(`${files.length} archivo(s) empaquetados en ZIP`);
    } catch (e: any) {
      getMsg()?.error(e?.message || "No se pudo armar el ZIP");
    } finally {
      setDownloadingAll(false);
    }
  }, [files, flujoId]);

  // ── Acciones — borrado ────────────────────────────────────────────────
  const handleDeleteOne = useCallback(
    async (file: FlujoFile) => {
      if (!flujoId) return;
      setDeletingKeys((prev) => new Set(prev).add(file.key));
      try {
        const updated = await eliminarFlujoArchivo(flujoId, file.key);
        setFiles(updated);
        setSelectedKeys((prev) => {
          const next = new Set(prev);
          next.delete(file.key);
          return next;
        });
      } finally {
        setDeletingKeys((prev) => {
          const next = new Set(prev);
          next.delete(file.key);
          return next;
        });
      }
    },
    [flujoId],
  );

  const handleDeleteSelected = useCallback(() => {
    const targets = files.filter((f) => selectedKeys.has(f.key));
    if (targets.length === 0) return;
    getModal()?.confirm({
      title: `¿Eliminar ${targets.length} archivo(s)?`,
      content:
        "Esta acción es definitiva — los archivos se borrarán del servidor y no se pueden recuperar.",
      okText: "Sí, eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        for (const file of targets) {
          // eslint-disable-next-line no-await-in-loop
          await handleDeleteOne(file);
        }
      },
    });
  }, [files, selectedKeys, handleDeleteOne]);

  // ── Acciones — abrir / previsualizar ─────────────────────────────────
  const openFile = useCallback((file: FlujoFile) => {
    if (file.kind === "image") {
      setPreviewImage(file.url);
    } else if (file.kind === "video") {
      setPreviewVideo(file);
    } else {
      // Docs: abrir en pestaña nueva (el browser usa su visor PDF nativo).
      window.open(file.url, "_blank", "noopener,noreferrer");
    }
  }, []);

  // ── Render helpers ────────────────────────────────────────────────────
  const headerActions = useMemo(() => {
    const hasFiles = files.length > 0;
    const hasSelection = selectedKeys.size > 0;
    return (
      <Space wrap size="small">
        <Checkbox
          indeterminate={partiallySelected}
          checked={allSelected}
          disabled={!hasFiles}
          onChange={(e) => toggleSelectAll(e.target.checked)}
        >
          <Text>
            {hasSelection
              ? `Seleccionados ${selectedKeys.size}/${files.length}`
              : "Seleccionar todo"}
          </Text>
        </Checkbox>
        <Tooltip title="Descargar la selección como ZIP">
          <Button
            size="small"
            icon={<DownloadOutlined />}
            disabled={!hasSelection}
            loading={downloadingAll}
            onClick={downloadSelectedAsZip}
          >
            Descargar ({selectedKeys.size})
          </Button>
        </Tooltip>
        <Tooltip title="Empaquetar todos los archivos en un solo ZIP">
          <Button
            size="small"
            icon={<DownloadOutlined />}
            disabled={!hasFiles}
            loading={downloadingAll}
            onClick={downloadAllAsZip}
          >
            Descargar todos
          </Button>
        </Tooltip>
        <Tooltip title="Eliminar definitivamente la selección">
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            disabled={!hasSelection}
            onClick={handleDeleteSelected}
          >
            Eliminar ({selectedKeys.size})
          </Button>
        </Tooltip>
      </Space>
    );
  }, [
    files.length,
    selectedKeys.size,
    partiallySelected,
    allSelected,
    toggleSelectAll,
    downloadSelectedAsZip,
    downloadAllAsZip,
    handleDeleteSelected,
    downloadingAll,
  ]);

  // ── Estado: flujo nuevo sin id ────────────────────────────────────────
  if (!flujoId) {
    return (
      <Alert
        type="info"
        showIcon
        message="Guarda el flujo antes de adjuntar archivos"
        description="Una vez creado, ábrelo para editar y vas a poder subir fotos, videos, PDFs y documentos (boletas, facturas, comprobantes) en esta sección."
      />
    );
  }

  return (
    <div className="flujo-files-manager">
      {/* ─── Zona de upload — drag&drop + click + Ctrl+V ─────────────── */}
      <DragDropUpload
        accept={FLUJO_ACCEPT}
        multiple
        loading={uploading}
        onFile={handleUpload}
        compact
        title="Arrastra fotos o documentos aquí para adjuntarlos"
        hint="También funcionan: click para elegir, o Ctrl+V para pegar (capturas, archivos copiados)"
      />

      {/* ─── Toolbar de selección + acciones bulk ─────────────────────── */}
      <div className="flujo-files-toolbar">
        <Space size="middle" wrap>
          <Badge
            count={files.length}
            showZero
            style={{ backgroundColor: files.length ? "#1677ff" : "#bfbfbf" }}
          >
            <Text strong style={{ paddingRight: 8 }}>
              Adjuntos
            </Text>
          </Badge>
          {loading && <Spin size="small" />}
        </Space>
        {headerActions}
      </div>

      {/* ─── Grid de archivos o estado vacío ───────────────────────────── */}
      {files.length === 0 && !loading ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Paragraph type="secondary" style={{ margin: 0 }}>
              Todavía no hay archivos adjuntos.
              <br />
              Sube fotos o documentos (boletas, facturas, comprobantes) para
              respaldar este movimiento.
            </Paragraph>
          }
        />
      ) : (
        <div className="flujo-files-grid">
          {files.map((file) => {
            const isSelected = selectedKeys.has(file.key);
            const isDeleting = deletingKeys.has(file.key);
            const { text: kindText, color: kindColor } = kindLabel(file);
            return (
              <div
                key={file.key}
                className={`flujo-files-card${isSelected ? " is-selected" : ""}`}
              >
                {/* Checkbox top-left para selección bulk */}
                <Checkbox
                  className="flujo-files-card-check"
                  checked={isSelected}
                  onChange={(e) => toggleSelected(file.key, e.target.checked)}
                  aria-label={`Seleccionar ${file.originalName}`}
                />

                {/* Thumbnail / ícono — click abre preview */}
                <button
                  type="button"
                  className="flujo-files-card-thumb"
                  onClick={() => openFile(file)}
                  title={`Abrir ${file.originalName}`}
                >
                  {file.kind === "image" && file.thumbUrl ? (
                    <img
                      src={file.thumbUrl}
                      alt={file.originalName}
                      loading="lazy"
                      draggable={false}
                    />
                  ) : file.kind === "video" && file.thumbUrl ? (
                    <div className="flujo-files-card-video-thumb">
                      <img
                        src={file.thumbUrl}
                        alt={file.originalName}
                        loading="lazy"
                        draggable={false}
                      />
                      <PlayCircleOutlined className="flujo-files-card-play" />
                    </div>
                  ) : (
                    <div className="flujo-files-card-icon">
                      <FileIcon file={file} size={48} />
                    </div>
                  )}
                </button>

                {/* Pie con nombre + tamaño + tag + acciones */}
                <div className="flujo-files-card-meta">
                  <Tooltip title={file.originalName}>
                    <Text ellipsis className="flujo-files-card-name">
                      {file.originalName}
                    </Text>
                  </Tooltip>
                  <div className="flujo-files-card-row">
                    <Tag color={kindColor} style={{ marginRight: 0 }}>
                      {kindText}
                    </Tag>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {formatBytes(file.size)}
                    </Text>
                  </div>
                  <Space size={4} style={{ marginTop: 6 }} wrap>
                    <Tooltip title="Abrir / previsualizar">
                      <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => openFile(file)}
                      />
                    </Tooltip>
                    <Tooltip title="Descargar">
                      <Button
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownloadOne(file)}
                      />
                    </Tooltip>
                    <Popconfirm
                      title="¿Eliminar este archivo?"
                      description="No se puede recuperar después."
                      okText="Eliminar"
                      cancelText="Cancelar"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => handleDeleteOne(file)}
                    >
                      <Tooltip title="Eliminar">
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          loading={isDeleting}
                        />
                      </Tooltip>
                    </Popconfirm>
                  </Space>
                </div>

                {/* Overlay loading mientras se elimina */}
                {isDeleting && (
                  <div className="flujo-files-card-overlay">
                    <Spin />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ─── Preview lightbox de imagen ────────────────────────────────── */}
      {previewImage && (
        <AntImage
          src={previewImage}
          style={{ display: "none" }}
          preview={{
            visible: !!previewImage,
            src: previewImage,
            onVisibleChange: (vis) => {
              if (!vis) setPreviewImage(null);
            },
          }}
        />
      )}

      {/* ─── Preview modal de video ────────────────────────────────────── */}
      <AntModal
        open={!!previewVideo}
        onCancel={() => setPreviewVideo(null)}
        title={previewVideo?.originalName ?? "Video"}
        footer={null}
        width={900}
        style={{ top: 24 }}
        destroyOnClose
      >
        {previewVideo && (
          <VideoPlayer
            src={previewVideo.url}
            previewSrc={previewVideo.previewUrl}
            nombreArchivo={previewVideo.originalName}
            sizeBytes={previewVideo.size}
            previewSizeBytes={previewVideo.previewSize}
            altoMaximo="calc(100dvh - 220px)"
            autoPlay
            playsInline
          />
        )}
      </AntModal>
    </div>
  );
};

export default FlujoFilesManager;
